import React, { useState, useEffect } from 'react';
import { Home, Users, Upload, User, ChevronLeft, ChevronRight, Info, Search, LogOut } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userEmailAtom, logoutAtom } from '../../store/auth';
import { testApiConnection } from '../../utils/testApiConnection';

interface SidebarProps {
  className?: string;
  onMobileClose?: () => void;
  isMobile?: boolean;
  onUploadClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ className = '', onMobileClose, isMobile = false, onUploadClick }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage or default to false
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [showSearchToast, setShowSearchToast] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail] = useAtom(userEmailAtom);
  const [, logout] = useAtom(logoutAtom);
  const [apiConnectionStatus, setApiConnectionStatus] = useState<{
    connected: boolean | null;
    testing: boolean;
  }>({ connected: null, testing: true });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Check API status
  useEffect(() => {
    const checkConnection = async () => {
      const result = await testApiConnection();
      setApiConnectionStatus({
        connected: result.success,
        testing: false,
      });
    };

    checkConnection();
    const interval = setInterval(checkConnection, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setShowSearchToast(true);
      setTimeout(() => setShowSearchToast(false), 3000);
    }
  };

  return (
    <div className={`${
      isMobile 
        ? 'w-64 bg-white shadow-xl border-r border-gray-200 flex flex-col h-full' 
        : `${isCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 flex flex-col h-full transition-all duration-300`
    } ${className}`}>
      {/* Search Bar - Show only on mobile */}
      {isMobile && (
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[var(--font-size-sm)]"
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          {/* Toast notification */}
          {showSearchToast && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-lg animate-in fade-in slide-in-from-top-2">
              <div className="flex items-start gap-2">
                <span className="text-lg">🚀</span>
                <div className="flex-1">
                  <p className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-blue-900">
                    Search is coming soon!
                  </p>
                  <p className="text-[var(--font-size-xs)] text-blue-700 mt-0.5">
                    We're building powerful search across all talent profiles.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {/* Toggle Button - Hide on mobile */}
          {!isMobile && (
            <li>
              <Button
                variant="ghost"
                onClick={toggleSidebar}
                className={`w-full ${isCollapsed ? 'justify-center p-2' : 'justify-start'} text-left hover:bg-gray-100`}
                title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
              >
                {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className={`h-4 w-4 ${isCollapsed ? '' : 'mr-3'}`} />}
                {!isCollapsed && 'Collapse'}
              </Button>
            </li>
          )}
          <li>
            <Button
              variant="ghost"
              className={`w-full ${
                isMobile 
                  ? 'justify-start text-left hover:bg-gray-100' 
                  : `${isCollapsed ? 'justify-center p-2' : 'justify-start'} text-left hover:bg-gray-100`
              } ${
                location.pathname === '/' ? 'bg-gray-100 text-gray-900' : ''
              }`}
              title={isCollapsed && !isMobile ? 'Dashboard' : ''}
              onClick={() => handleNavigation('/')}
            >
              <Home className={`h-4 w-4 ${isMobile || !isCollapsed ? 'mr-3' : ''}`} />
              {(isMobile || !isCollapsed) && 'Dashboard'}
            </Button>
          </li>
          <li>
            <Button
              variant="ghost"
              className={`w-full ${
                isMobile 
                  ? 'justify-start text-left hover:bg-gray-100' 
                  : `${isCollapsed ? 'justify-center p-2' : 'justify-start'} text-left hover:bg-gray-100`
              } ${
                location.pathname === '/talents' ? 'bg-gray-100 text-gray-900' : ''
              }`}
              title={isCollapsed && !isMobile ? 'Talents' : ''}
              onClick={() => handleNavigation('/talents')}
            >
              <Users className={`h-4 w-4 ${isMobile || !isCollapsed ? 'mr-3' : ''}`} />
              {(isMobile || !isCollapsed) && 'Talents'}
            </Button>
          </li>


          <li>
            <Button
              variant="ghost"
              className={`w-full ${
                isMobile
                  ? 'justify-start text-left hover:bg-gray-100'
                  : `${isCollapsed ? 'justify-center p-2' : 'justify-start'} text-left hover:bg-gray-100`
              }`}
              title={isCollapsed && !isMobile ? 'Upload' : ''}
              onClick={() => {
                if (onUploadClick) {
                  onUploadClick();
                  if (isMobile && onMobileClose) {
                    onMobileClose();
                  }
                }
              }}
            >
              <Upload className={`h-4 w-4 ${isMobile || !isCollapsed ? 'mr-3' : ''}`} />
              {(isMobile || !isCollapsed) && 'Upload'}
            </Button>
          </li>

          <li>
            <Button
              variant="ghost"
              className={`w-full ${
                isMobile
                  ? 'justify-start text-left hover:bg-gray-100'
                  : `${isCollapsed ? 'justify-center p-2' : 'justify-start'} text-left hover:bg-gray-100`
              } ${
                location.pathname === '/about' ? 'bg-gray-100 text-gray-900' : ''
              }`}
              title={isCollapsed && !isMobile ? 'About' : ''}
              onClick={() => handleNavigation('/about')}
            >
              <Info className={`h-4 w-4 ${isMobile || !isCollapsed ? 'mr-3' : ''}`} />
              {(isMobile || !isCollapsed) && 'About'}
            </Button>
          </li>
        </ul>
      </nav>

      {/* User Profile Section */}
      <div className="p-4 border-t border-gray-200">
        {(isCollapsed && !isMobile) ? (
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center" title={userEmail || 'User'}>
              <User className="h-4 w-4 text-white" />
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-gray-900">{userEmail || 'User'}</p>
            </div>
          </div>
        )}
      </div>

      {/* API Status Indicator and Logout Button */}
      <div className="border-t border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            {!isCollapsed && !isMobile ? (
              <>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  apiConnectionStatus.testing ? 'bg-yellow-500 animate-pulse' :
                  apiConnectionStatus.connected ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--font-size-xs)] text-gray-500">API Status</p>
                </div>
              </>
            ) : (
              <div className={`w-2 h-2 rounded-full ${
                apiConnectionStatus.testing ? 'bg-yellow-500 animate-pulse' :
                apiConnectionStatus.connected ? 'bg-green-500' : 'bg-red-500'
              }`} title="API Status" />
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="hover:bg-red-50 text-red-600 p-2 h-8 w-8 flex-shrink-0"
            title="Logout"
            onClick={async () => {
              try {
                await logout();
                if (isMobile && onMobileClose) {
                  onMobileClose();
                }
              } catch (error) {
                console.error('Logout failed:', error);
              }
            }}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;